// Mock data + types for Clubes/Grupos.
// Hoje serve como semente para o protótipo navegável; quando o backend de clubes
// estiver pronto, estes mocks são substituídos pelos resultados das chamadas API.

export type ClubMode = 'group-read' | 'shared-habit';

export interface ClubAdmin {
  name: string;
  avatar: string;
  verified?: boolean;
  role?: string;
}

export interface ClubBook {
  title: string;
  author: string;
  pages: number;
  coverHue: number;
  coverStyle: string;
  startDate?: string;
  endDate?: string;
  collectivePct: number;
  yourPct: number;
  pagesBehind?: number;
}

export interface ClubTopBook {
  title: string;
  author: string;
  coverHue: number;
  coverStyle: string;
}

export interface ClubNextEvent {
  date: string;
  title: string;
  link: string;
}

export type SeriesBookState = 'done' | 'current' | 'planned' | 'tbd';

export interface ClubSeriesBook {
  id: string;
  position: number;
  state: SeriesBookState;
  title?: string;
  author?: string;
  coverHue?: number;
  coverStyle?: string;
  coverUrl?: string;
  startDate?: string;
  endDate?: string;
  collectivePct?: number;
  yourPct?: number;
}

export interface ClubSeries {
  totalPlanned: number;
  startDate?: string;
  duration?: { count: number; unit: string };
  currentIndex?: number;
  daysToNext?: number;
  books: ClubSeriesBook[];
}

// MySeries: fila individual do membro em Clube Avulso (shared-habit).
// Reusa ClubSeriesBook + campos extras: memberBookId (id em club_member_books)
// e bookId (id do livro pessoal em books).
export interface ClubMySeriesBook extends ClubSeriesBook {
  memberBookId?: string;
  bookId?: string;
}

export interface ClubMySeries {
  userId: string;
  isMine: boolean;
  totalPlanned: number;
  currentIndex: number;
  books: ClubMySeriesBook[];
}

export interface Club {
  id: string;
  name: string;
  mode: ClubMode;
  admin: ClubAdmin;
  members: number;
  activeToday: number;
  readingNow: number;
  cover: { hue: number; style: string };
  book?: ClubBook;
  myBook?: any;
  series?: ClubSeries;
  /** Avulso: fila individual do membro. Adicionado em F2. */
  mySeries?: ClubMySeries;
  weeklyPages?: number;
  weeklyHours?: number;
  topBooks?: ClubTopBook[];
  yourProgress: number;
  streak: number;
  nextEvent?: ClubNextEvent;
}

export interface PendingInvite {
  id: string;
  clubName: string;
  invitedBy: string;
  members: number;
  hue: number;
}

export interface ExploreClub {
  id: string;
  name: string;
  members: number;
  hue: number;
  style: string;
  tag: string;
}

export interface RankingRow {
  rank: number;
  name: string;
  avatar: string;
  pages: number;
  streak: number;
  isYou: boolean;
  badge?: boolean;
  inactive?: boolean;
  lastSeenDays?: number;
}

export interface PostReactions {
  tocou: number;
  aprendi: number;
  fogo: number;
  salvar: number;
}

export const CLUBS: Club[] = [
  {
    id: 'c1',
    name: 'Manhãs com Bonhoeffer',
    mode: 'group-read',
    admin: { name: 'Pr. Lucas Andrade', avatar: 'L', verified: true, role: 'Pastor verificado' },
    members: 127,
    activeToday: 23,
    readingNow: 12,
    cover: { hue: 22, style: 'paper' },
    book: {
      title: 'Discipulado',
      author: 'Dietrich Bonhoeffer',
      pages: 312,
      coverHue: 22,
      coverStyle: 'cloth',
      startDate: '15 abr',
      endDate: '15 mai',
      collectivePct: 47,
      yourPct: 38,
      pagesBehind: 23,
    },
    yourProgress: 38,
    streak: 47,
    nextEvent: { date: 'Quinta · 22 mai · 20h', title: 'Encontro online — Cap. 8 e 9', link: 'Zoom' },
  },
  {
    id: 'c2',
    name: 'Leitores da Madrugada',
    mode: 'shared-habit',
    admin: { name: 'Marina Vasquez', avatar: 'M', verified: false, role: 'Líder' },
    members: 84,
    activeToday: 31,
    readingNow: 8,
    cover: { hue: 280, style: 'linen' },
    weeklyPages: 4271,
    weeklyHours: 96,
    topBooks: [
      { title: 'O Hobbit', author: 'Tolkien', coverHue: 140, coverStyle: 'paper' },
      { title: 'Sapiens', author: 'Harari', coverHue: 40, coverStyle: 'linen' },
      { title: 'Mil Sóis', author: 'Hosseini', coverHue: 20, coverStyle: 'cloth' },
    ],
    streak: 47,
    yourProgress: 0,
  },
  {
    id: 'c3',
    name: 'Estante de Setembro',
    mode: 'group-read',
    admin: { name: 'Profa. Helena R.', avatar: 'H', verified: true, role: 'Educadora' },
    members: 42,
    activeToday: 8,
    readingNow: 5,
    cover: { hue: 200, style: 'paper' },
    book: {
      title: 'Cem Anos de Solidão',
      author: 'García Márquez',
      pages: 412,
      coverHue: 200,
      coverStyle: 'paper',
      startDate: '1 mai',
      endDate: '15 jun',
      collectivePct: 12,
      yourPct: 18,
      pagesBehind: -24,
    },
    yourProgress: 18,
    streak: 6,
  },
];

export const PENDING_INVITES: PendingInvite[] = [
  { id: 'i1', clubName: 'Café & Filosofia', invitedBy: 'Pedro Sá', members: 18, hue: 340 },
  { id: 'i2', clubName: 'Grupo de Jejum 21d', invitedBy: 'Pra. Sara M.', members: 56, hue: 18 },
];

export const EXPLORE_CLUBS: ExploreClub[] = [
  { id: 'e1', name: 'Lendo a Bíblia em 1 ano', members: 1284, hue: 38, style: 'cloth', tag: 'Devocional' },
  { id: 'e2', name: 'Clássicos Russos', members: 312, hue: 220, style: 'linen', tag: 'Literatura' },
  { id: 'e3', name: 'Não-ficção 2026', members: 547, hue: 140, style: 'paper', tag: 'Ensaio' },
  { id: 'e4', name: 'Mães que leem', members: 891, hue: 340, style: 'paper', tag: 'Comunidade' },
];

export const RANKING: RankingRow[] = [
  { rank: 1, name: 'Beatriz N.', avatar: 'B', pages: 184, streak: 23, isYou: false },
  { rank: 2, name: 'Tiago Lima', avatar: 'T', pages: 162, streak: 47, isYou: false },
  { rank: 3, name: 'Pra. Cláudia', avatar: 'C', pages: 158, streak: 19, isYou: false, badge: true },
  { rank: 4, name: 'Davi Mendes', avatar: 'D', pages: 142, streak: 12, isYou: false },
  { rank: 5, name: 'Marina', avatar: 'M', pages: 124, streak: 9, isYou: true },
  { rank: 6, name: 'Júlia Reis', avatar: 'J', pages: 118, streak: 8, isYou: false },
  { rank: 7, name: 'Ricardo P.', avatar: 'R', pages: 102, streak: 14, isYou: false },
  { rank: 8, name: 'Helena S.', avatar: 'H', pages: 98, streak: 0, isYou: false, inactive: true, lastSeenDays: 9 },
  { rank: 9, name: 'Marcos V.', avatar: 'M', pages: 87, streak: 0, isYou: false, inactive: true, lastSeenDays: 14 },
  { rank: 10, name: 'Ana Cristina', avatar: 'A', pages: 81, streak: 0, isYou: false, inactive: true, lastSeenDays: 21 },
];

export interface InsightAuthor {
  name: string;
  avatar: string;
  verified?: boolean;
  role?: string;
}

export interface InsightPost {
  id: string;
  author: InsightAuthor;
  chapter?: string;
  timeAgo: string;
  text: string;
  reactions: PostReactions;
  comments: number;
  pinned?: boolean;
}

export const INSIGHTS: InsightPost[] = [
  {
    id: 'p1',
    author: { name: 'Pr. Lucas Andrade', avatar: 'L', verified: true, role: 'Pastor' },
    chapter: 'Cap. 7',
    timeAgo: 'há 2h',
    text: 'Quando Bonhoeffer fala em "graça barata", entendi pela primeira vez por que tantas vezes falo de perdão sem mudar de vida. A graça custou tudo a Cristo — recebê-la sem custo é desonra. Ler isso em voz alta com o caderno aberto mudou minha manhã.',
    reactions: { tocou: 24, aprendi: 18, fogo: 12, salvar: 7 },
    comments: 12,
    pinned: true,
  },
  {
    id: 'p2',
    author: { name: 'Beatriz N.', avatar: 'B', verified: false },
    chapter: 'Cap. 5',
    timeAgo: 'há 5h',
    text: 'O sermão do monte como um todo, e não como peças soltas. Tô relendo a parte sobre os bem-aventurados pela terceira vez e ainda dói.',
    reactions: { tocou: 9, aprendi: 14, fogo: 5, salvar: 3 },
    comments: 4,
  },
  {
    id: 'p3',
    author: { name: 'Tiago Lima', avatar: 'T', verified: false },
    chapter: 'Cap. 8',
    timeAgo: 'há 1d',
    text: 'A ideia de que o discípulo não escolhe seu próprio caminho — ele responde a um chamado. Pesado pra quem cresceu valorizando autonomia acima de tudo.',
    reactions: { tocou: 31, aprendi: 22, fogo: 9, salvar: 11 },
    comments: 18,
  },
];

// ─── Estudo do livro: marcos + posts ───

export type PostKind =
  | 'reflection' | 'citation' | 'question' | 'quiz'
  | 'question-box' | 'leader-answer' | 'insight-final';

export interface QuizOption {
  id: string;
  text: string;
  votes: number;
  correct?: boolean;
}

export interface StudyPost {
  id: string;
  kind: PostKind;
  author: InsightAuthor;
  page?: number;
  timeAgo: string;
  text?: string;
  question?: string;
  options?: QuizOption[];
  totalVotes?: number;
  hasGabarito?: boolean;
  prompt?: string;
  closesIn?: string;
  questionsReceived?: number;
  comment?: string;
  responses?: number;
  reactions?: PostReactions;
  comments?: number;
  milestoneLabel?: string;
  title?: string;
}

export interface MilestoneInsightFinal {
  id: string;
  author: InsightAuthor;
  page: number;
  milestoneLabel: string;
  title: string;
  text: string;
  reactions: PostReactions;
  comments: number;
}

export interface Milestone {
  id: string;
  n: number;
  label: string;
  deadline?: string;
  closedOn?: string;
  state: 'active' | 'closed';
  chapterRef?: string;
  yourPage?: number;
  targetPage?: number;
  posts?: StudyPost[];
  insightFinal?: MilestoneInsightFinal;
  totalPosts?: number;
}

export const MILESTONES: Milestone[] = [
  {
    id: 'm3', n: 3, label: 'Página 200', deadline: '15 mai',
    chapterRef: 'Cap. 9-12', state: 'active',
    yourPage: 87, targetPage: 200,
    posts: [
      {
        id: 'p3-leader-answer', kind: 'leader-answer',
        author: { name: 'Pr. Lucas Andrade', avatar: 'L', verified: true, role: 'Pastor' },
        page: 178, timeAgo: 'há 2h',
        question: 'Como conciliar a "graça custosa" com a paz que Jesus oferece?',
        text: 'A graça custosa não tira a paz — ela aprofunda. A paz superficial é evitar conflito; a paz que Cristo dá é a certeza de estar no caminho certo, mesmo quando ele exige tudo. Bonhoeffer não opõe paz e custo: ele os funde.',
        reactions: { tocou: 18, aprendi: 24, fogo: 7, salvar: 12 },
        comments: 8,
      },
      {
        id: 'p3-quiz', kind: 'quiz',
        author: { name: 'Pr. Lucas Andrade', avatar: 'L', verified: true, role: 'Pastor' },
        page: 174, timeAgo: 'há 5h',
        question: 'Segundo Bonhoeffer no capítulo 9, o que distingue a graça custosa da graça barata?',
        options: [
          { id: 'a', text: 'A intensidade do arrependimento sentido', votes: 18 },
          { id: 'b', text: 'O custo concreto que ela impõe à vida do discípulo', votes: 64, correct: true },
          { id: 'c', text: 'O número de vezes que se confessa o pecado', votes: 8 },
        ],
        totalVotes: 90,
        hasGabarito: true,
      },
      {
        id: 'p3-question-box', kind: 'question-box',
        author: { name: 'Pr. Lucas Andrade', avatar: 'L', verified: true, role: 'Pastor' },
        timeAgo: 'há 1d',
        prompt: 'Dúvidas sobre o que foi lido até a página 200?',
        closesIn: '2 dias',
        questionsReceived: 14,
      },
      {
        id: 'p3-citation', kind: 'citation',
        author: { name: 'Beatriz N.', avatar: 'B' },
        page: 167, timeAgo: 'há 1d',
        text: 'A graça é gratuita, mas não é barata. A graça custou ao Filho de Deus a sua vida.',
        comment: 'Lendo essa frase em pé. Tive que parar e respirar.',
        reactions: { tocou: 32, aprendi: 11, fogo: 15, salvar: 22 },
        comments: 6,
      },
      {
        id: 'p3-question', kind: 'question',
        author: { name: 'Tiago Lima', avatar: 'T' },
        page: 152, timeAgo: 'há 2d',
        text: 'Vocês sentiram que o Cap. 10 muda o tom da obra? Pra mim, foi como se Bonhoeffer falasse direto, sem mediação. Como leram?',
        responses: 7,
        reactions: { tocou: 4, aprendi: 9, fogo: 2, salvar: 1 },
        comments: 7,
      },
      {
        id: 'p3-reflection', kind: 'reflection',
        author: { name: 'Marina', avatar: 'M' },
        page: 134, timeAgo: 'há 3d',
        text: 'Cheguei na página 134 e o que aprendi até aqui é que minha definição de "seguir Jesus" sempre teve uma cláusula escondida — "desde que não me custe demais". Bonhoeffer me tira essa cláusula.',
        reactions: { tocou: 19, aprendi: 14, fogo: 8, salvar: 5 },
        comments: 4,
      },
    ],
  },
  {
    id: 'm2', n: 2, label: 'Página 100', closedOn: '30 abr',
    state: 'closed',
    insightFinal: {
      id: 'p2-final',
      author: { name: 'Pra. Cláudia', avatar: 'C', verified: true, role: 'Líder' },
      page: 99, milestoneLabel: 'Marco 2 — Pág. 100',
      title: 'O que carrego das primeiras 100 páginas',
      text: 'Discipulado começou pra mim como um livro sobre teologia. Termina essa primeira fase como um livro sobre coragem. A coragem de assumir o nome de Cristo num tempo em que o cristianismo virou cultura confortável. As primeiras 100 páginas me ensinaram a desconfiar do que parece familiar demais. Vou começar a próxima fase com o caderno aberto e mais lentidão.',
      reactions: { tocou: 47, aprendi: 38, fogo: 22, salvar: 19 },
      comments: 23,
    },
    totalPosts: 38,
  },
  {
    id: 'm1', n: 1, label: 'Página 50', closedOn: '22 abr',
    state: 'closed',
    insightFinal: {
      id: 'p1-final',
      author: { name: 'Davi Mendes', avatar: 'D' },
      page: 49, milestoneLabel: 'Marco 1 — Pág. 50',
      title: 'Os primeiros 50 dias',
      text: 'A introdução do livro me parecia árida. Cheguei na página 50 e percebi que era exatamente o ponto: Bonhoeffer não quer me seduzir. Quer me convocar.',
      reactions: { tocou: 28, aprendi: 24, fogo: 11, salvar: 9 },
      comments: 14,
    },
    totalPosts: 22,
  },
];

export const STUDY_FILTERS = [
  { id: 'all', label: 'Tudo', icon: null },
  { id: 'finals', label: 'Insights Finais', icon: '🎯' },
  { id: 'quiz', label: 'Quiz', icon: '📊' },
  // 'questions' agrega Caixa de Perguntas + respostas do líder. Pergunta pública
  // (kind 'question') foi removida — Caixa cobre o caso de uso.
  { id: 'questions', label: 'Caixa de perguntas', icon: '💬' },
  { id: 'leader', label: 'Líder', icon: '✨' },
  { id: 'saved', label: 'Salvos', icon: '📌' },
] as const;

export const POST_TYPES: Record<PostKind, { icon: string; label: string; tone: string; textColor: string }> = {
  reflection:     { icon: '💡', label: 'Reflexão',           tone: 'oklch(0.78 0.06 60)',  textColor: 'oklch(0.42 0.07 50)' },
  citation:       { icon: '📖', label: 'Citação',            tone: 'oklch(0.78 0.05 80)',  textColor: 'oklch(0.40 0.05 70)' },
  question:       { icon: '❓', label: 'Pergunta',           tone: 'oklch(0.76 0.06 40)',  textColor: 'oklch(0.40 0.07 35)' },
  quiz:           { icon: '📊', label: 'Quiz',               tone: 'oklch(0.74 0.07 30)',  textColor: 'oklch(0.40 0.09 25)' },
  'question-box': { icon: '💬', label: 'Caixa de perguntas', tone: 'oklch(0.76 0.05 350)', textColor: 'oklch(0.40 0.06 350)' },
  'leader-answer':{ icon: '✨', label: 'Resposta do Líder',  tone: 'oklch(0.72 0.10 30)',  textColor: 'oklch(0.38 0.13 25)' },
  'insight-final':{ icon: '🎯', label: 'Insight Final',      tone: 'oklch(0.70 0.13 30)',  textColor: 'oklch(0.36 0.14 25)' },
};

export interface QuestionBoxItem {
  id: string;
  text: string;
  receivedAt: string;
  status: 'pending' | 'answered';
  answer?: string;
}

export const QUESTION_BOX_ITEMS: QuestionBoxItem[] = [
  { id: 'q1', text: 'Como conciliar a "graça custosa" com a paz que Jesus oferece em João 14? Tô lendo um e parece o oposto do outro.', receivedAt: 'há 4h', status: 'pending' },
  { id: 'q2', text: 'O capítulo 9 menciona o "discipulado de cruz". Isso é a mesma coisa que sofrimento por escolha?', receivedAt: 'há 8h', status: 'pending' },
  { id: 'q3', text: 'Bonhoeffer foi morto pelos nazistas. Como isso muda a leitura do livro?', receivedAt: 'há 1d', status: 'pending' },
  { id: 'q4', text: 'O que ele quer dizer com "obediência simples"? Parece simplista demais pra mim.', receivedAt: 'há 1d', status: 'pending' },
  { id: 'q5', text: 'Pode explicar a diferença entre lei e graça segundo o Bonhoeffer?', receivedAt: 'há 3d', status: 'answered', answer: 'A graça custosa não tira a paz — ela aprofunda. A paz superficial é evitar conflito; a paz que Cristo dá é a certeza de estar no caminho certo, mesmo quando ele exige tudo.' },
];
